import { StyledPropsWithChildren } from '@front10';
import { Context } from '@front10/helpers/dist/Context';
import useStyles from '@front10/helpers/dist/UseStyles';
import { getUuid } from '@front10/helpers/dist/uuid';
import React, { useContext, useEffect, useRef } from 'react';
import { getResizedImage } from '../../../../helpers/images';
import { useLazyLoading } from '../../../containers/_commons/lazyLoadImage';
import Box from '../../../elements/Box';
import useWindowDimensions from './../../../hooks/useWindowDimensions';

type ImageProps = JSX.IntrinsicElements['img'];
type SelectedProps = Pick<ImageProps, 'alt'>;

interface ImageResizingParams {
  worker: string; // Base path of the image worker
  height: number;
  width: number;
}

interface AddedProps {
  /**
   * URI of the image.
   */
  destinationImage: ImageProps['src'];
  /**
   * Height of Destination Image
   */
  heightDestinationImage: ImageProps['height'];
  /**
   * Width of Destination Image
   */
  widthDestinationImage: ImageProps['width'];
  /**
   * Destination City
   */
  destinationCity: string;
  /**
   *  Children of the component
   */
  pathNoImageFound: string;
  /**
   * Params for image resizing optimizations
   */
  imageResizingParams: ImageResizingParams;
  /**
   * Enable lazy loading
   */
  lazyLoadingEnable: boolean;
  /**
   * Forcing to load the images by https
   */
  forceHttpsProtocol: boolean;
  containerSettings: $TSFixMe;
  autoResizingImage?: boolean;
  autoResizeImageParams?: any;
}

type Props = StyledPropsWithChildren<AddedProps & SelectedProps>;

const FareItemOriginDestinationImage = (props: Props) => {
  const {
    destinationCity,
    heightDestinationImage,
    widthDestinationImage,
    destinationImage = '',
    imageResizingParams,
    pathNoImageFound,
    lazyLoadingEnable,
    forceHttpsProtocol,
    containerSettings,
    autoResizingImage = false,
    autoResizeImageParams = {},
    children
  } = props;

  const { direction, theme } = useContext(Context);
  const [className, style] = useStyles(props, { direction, theme });
  const { observer } = useLazyLoading();
  let imageURL = destinationImage;
  if (forceHttpsProtocol) {
    imageURL = destinationImage?.replace('http://', 'https://');
  }

  const isInInterval = (dimensions: any, device: any) => {
    if (device.width[0] <= dimensions.width && dimensions.width <= device.width[1]) {
      return true;
    }
    return false;
  };

  const isValidDeviceSetting = (device: any) => {
    return device !== undefined && device.width !== undefined && device.width.length === 2;
  };

  const dimensions = useWindowDimensions();

  const {
    desktopImageResizingParams = {},
    deviceViewPortSize = null,
    mobileImageResizingParams = {},
    tabletImageResizingParams = {}
  } = autoResizeImageParams;

  let newImageResizingParams = { ...imageResizingParams };
  if (!!autoResizingImage) {
    if (deviceViewPortSize !== null) {
      const { mobile, desktop, tablet } = deviceViewPortSize;
      if (isValidDeviceSetting(mobile) && isInInterval(dimensions, mobile)) {
        newImageResizingParams = {
          ...mobileImageResizingParams
        };
      } else if (isValidDeviceSetting(tablet) && isInInterval(dimensions, tablet)) {
        newImageResizingParams = {
          ...tabletImageResizingParams
        };
      } else if (isValidDeviceSetting(desktop) && isInInterval(dimensions, desktop)) {
        newImageResizingParams = {
          ...desktopImageResizingParams
        };
      }
    }
  }

  const imageContainerStyles = { paddingBottom: '100%' };
  const id = `fr-image-${getUuid()}`;
  const imageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { current } = imageRef;
    if (observer && lazyLoadingEnable && current) {
      observer.observe(current);
    }
  });

  const loadingImageStyle = {
    ...style,
    height: `${heightDestinationImage}%`,
    width: `${widthDestinationImage}%`,
    backgroundColor: 'lightgray',
    animation: 'pulse 1s infinite ease-in-out',
    backgroundImage: !lazyLoadingEnable
      ? `url(${getResizedImage(imageURL, newImageResizingParams)})`
      : undefined
  };

  return (
    <Box
      id={id}
      height="100"
      position="relative"
      style={imageContainerStyles}
      className="fareitem-destination-image-container"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        role={destinationCity ? 'img' : undefined}
        ariaLabel={destinationCity}
        className={`${className} fareitem-destination-image image`}
        style={loadingImageStyle}
        dataSrc={getResizedImage(imageURL, newImageResizingParams)}
        dataDefaultImage={getResizedImage(pathNoImageFound, newImageResizingParams)}
        focusReferences={imageRef}
        {...containerSettings}
      >
        {children}
      </Box>
    </Box>
  );
};

FareItemOriginDestinationImage.defaultProps = {
  destinationImage: '',
  heightDestinationImage: null,
  widthDestinationImage: null,
  destinationCity: '',
  pathNoImageFound:
    'https://res.cloudinary.com/dakp804eh/image/upload/v1561045947/airmodules/Utils/NoImageFound.png',
  imageResizingParams: {
    worker: 'https://assets.airtrfx.com/cdn-cgi/image/',
    height: 500,
    width: 500,
    quality: 80,
    fit: 'crop',
    format: 'auto',
    opt: true
  },
  lazyLoadingEnable: true,
  forceHttpsProtocol: true
};

FareItemOriginDestinationImage.componentName = 'FareItemOriginDestinationImage';

export default FareItemOriginDestinationImage;
